import React from "react";

// SubjectAchievement — curriculum-grounded, soft-3D glossy subject illustrations on a
// white glass circular surface. Faithful port of the design system's subject illustration.
// Two-level subjects (mathematics, religiousCulture, english) honor `level`
// (ms/hs); every other subject has one canonical illustration.
const SUBJECTS: Record<string, any> = {
  mathematics: { label: "Mathematics", main: "#F2935A", light: "#FCC987", deep: "#D55A2E", accent: "#56BBCB", ink: "#7A3A14", cream: "#FFF1E2" },
  science: { label: "Science", main: "#A98BE6", light: "#CDB6F5", deep: "#6E3FBC", accent: "#5BC08A", ink: "#3E2A6B", cream: "#F4ECFD" },
  chemistry: { label: "Chemistry", main: "#4FC6C0", light: "#93E0DB", deep: "#16847F", accent: "#F2935A", ink: "#0F5C59", cream: "#E6FAF8" },
  biology: { label: "Biology", main: "#5BC08A", light: "#A0E0BB", deep: "#268655", accent: "#F2C744", ink: "#1E5C3F", cream: "#E9FAF0" },
  physics: { label: "Physics", main: "#7B74E0", light: "#ADA8F2", deep: "#544CC4", accent: "#F2935A", ink: "#332E8C", cream: "#ECEBFD" },
  socialStudies: { label: "Social Studies", main: "#5B9BE8", light: "#A0C6F5", deep: "#2A63BD", accent: "#5BC08A", ink: "#1E4A87", cream: "#E7F0FD" },
  history: { label: "History", main: "#CFA552", light: "#E8CE8A", deep: "#94632A", accent: "#5B9BE8", ink: "#5C3F18", cream: "#FAF2E0" },
  geography: { label: "Geography", main: "#F2885F", light: "#F8B996", deep: "#BB4F34", accent: "#4FC6C0", ink: "#7A3320", cream: "#FFEBE2" },
  turkish: { label: "Turkish", main: "#F2C744", light: "#F8E08C", deep: "#C5921A", accent: "#E8643B", ink: "#7A5E0F", cream: "#FFF9E2" },
  literature: { label: "Literature", main: "#E8B84F", light: "#F4D67E", deep: "#B07F1A", accent: "#5B9BE8", ink: "#7A5E0F", cream: "#FFF8E2" },
  english: { label: "English", main: "#7E94A6", light: "#B4C5D1", deep: "#516373", accent: "#4FC6C0", ink: "#3A4753", cream: "#F1F6F8" },
  religiousCulture: { label: "Religious Culture", main: "#E87BA8", light: "#F4AECB", deep: "#B23468", accent: "#F2C744", ink: "#7A2348", cream: "#FDEAF2" },
  philosophy: { label: "Philosophy", main: "#9B7BE0", light: "#C6AEF3", deep: "#623AAB", accent: "#F2C744", ink: "#3E2A6B", cream: "#F3ECFD" },
  generalKnowledge: { label: "General Knowledge", main: "#54BBCB", light: "#9BE2EA", deep: "#0F899C", accent: "#F2C744", ink: "#0F5C66", cream: "#E6F8FB" },
  duel: { label: "Duel", main: "#F2865F", light: "#F8B996", deep: "#C44A2A", accent: "#F2C744", ink: "#7A3320", cream: "#FFEBE2" },
  turkishComp: { label: "Turkish", main: "#F2C744", light: "#F8E08C", deep: "#C5921A", accent: "#E8643B", ink: "#7A5E0F", cream: "#FFF9E2" },
};

const LEVEL_ALIAS: Record<string, string> = {
  ms: "ms", middle: "ms", "middle-school": "ms", "5-8": "ms", junior: "ms",
  hs: "hs", high: "hs", "high-school": "hs", "9-12": "hs", senior: "hs",
};
const TWO_LEVEL: Record<string, boolean> = { mathematics: true, religiousCulture: true, english: true };
const UNIFIED: Record<string, string> = {
  science: "ms", chemistry: "ms", biology: "hs", physics: "hs", turkish: "ms",
  literature: "hs", english: "hs", socialStudies: "ms", history: "ms",
  geography: "ms", philosophy: "ms",
};

const defs = (p: any, u: string) => `<defs>
  <linearGradient id="m${u}" x1="0.22" y1="0.04" x2="0.62" y2="1">
    <stop offset="0" stop-color="${p.light}"/><stop offset="0.55" stop-color="${p.main}"/><stop offset="1" stop-color="${p.deep}"/>
  </linearGradient>
  <linearGradient id="d${u}" x1="0.2" y1="0" x2="0.6" y2="1">
    <stop offset="0" stop-color="${p.main}"/><stop offset="1" stop-color="${p.deep}"/>
  </linearGradient>
  <linearGradient id="a${u}" x1="0.2" y1="0.05" x2="0.6" y2="1">
    <stop offset="0" stop-color="#ffffff" stop-opacity="0.55"/><stop offset="0.2" stop-color="${p.accent}"/><stop offset="1" stop-color="${p.accent}"/>
  </linearGradient>
  <linearGradient id="c${u}" x1="0" y1="0" x2="0" y2="1">
    <stop offset="0" stop-color="#ffffff"/><stop offset="1" stop-color="${p.cream}"/>
  </linearGradient>
  <radialGradient id="gl${u}" cx="0.36" cy="0.18" r="0.9">
    <stop offset="0" stop-color="#ffffff" stop-opacity="0.55"/><stop offset="0.5" stop-color="#ffffff" stop-opacity="0.08"/><stop offset="1" stop-color="#ffffff" stop-opacity="0"/>
  </radialGradient>
  <filter id="dp${u}" x="-50%" y="-45%" width="200%" height="205%">
    <feDropShadow dx="0" dy="2.2" stdDeviation="2.3" flood-color="#0E3A45" flood-opacity="0.22"/>
  </filter>
  <filter id="vol${u}" x="-35%" y="-35%" width="170%" height="170%">
    <feGaussianBlur in="SourceAlpha" stdDeviation="1.3" result="vb"/>
    <feSpecularLighting in="vb" surfaceScale="3.2" specularConstant="0.66" specularExponent="11" lighting-color="#ffffff" result="vs"><fePointLight x="-16" y="-24" z="56"/></feSpecularLighting>
    <feComposite in="vs" in2="SourceAlpha" operator="in" result="vsc"/>
    <feMerge><feMergeNode in="SourceGraphic"/><feMergeNode in="vsc"/></feMerge>
  </filter>
  <filter id="sh${u}" x="-45%" y="-25%" width="190%" height="170%"><feGaussianBlur stdDeviation="1.7"/></filter>
  <radialGradient id="glow${u}" cx="0.5" cy="0.54" r="0.55">
    <stop offset="0" stop-color="${p.main}" stop-opacity="0.7"/><stop offset="1" stop-color="${p.main}" stop-opacity="0"/>
  </radialGradient>
  <filter id="bl${u}" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="3"/></filter>
</defs>`;

const sh = (u: string, cx: number, cy: number, rx: number, ry: number) =>
  `<ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="#0F4859" opacity="0.15" filter="url(#sh${u})"/>`;

const ILLO: Record<string, Record<string, (p: any, u: string) => string>> = {
  generalKnowledge: {
    ms: (p, u) => `${sh(u, 32, 53, 16, 3)}
      <path d="M22 23c-7 0-10-3-10-8h10M42 23c7 0 10-3 10-8H42" fill="none" stroke="url(#d${u})" stroke-width="3.4" stroke-linecap="round"/>
      <path d="M21 12h22v9a11 11 0 0 1-22 0z" fill="url(#m${u})"/><path d="M21 12h22v9a11 11 0 0 1-22 0z" fill="url(#gl${u})"/>
      <rect x="29.5" y="33" width="5" height="8" fill="url(#d${u})"/><path d="M23 50l3-7h12l3 7z" fill="url(#d${u})"/>
      <path d="M21 50h22" stroke="${p.deep}" stroke-width="2.6" stroke-linecap="round"/>
      <path d="M32 15l1.5 3.4 3.7.3-2.8 2.4.9 3.6L32 25.3 28.7 27.2l.9-3.6-2.8-2.4 3.7-.3z" fill="url(#a${u})"/>`,
  },
  duel: {
    ms: (p, u) => `${sh(u, 32, 53, 18, 3)}
      <path d="M32 12 A20 20 0 0 0 32 52 Z" fill="url(#m${u})"/><path d="M32 12 A20 20 0 0 1 32 52 Z" fill="url(#d${u})"/>
      <circle cx="32" cy="32" r="20" fill="url(#gl${u})"/>
      <path d="M33 11 L27 32 L33 32 L31 53 L40 30 L34 30 Z" fill="url(#a${u})" stroke="#fff" stroke-width="0.8"/>
      <text x="21" y="38" text-anchor="middle" font-family="Quicksand, sans-serif" font-size="15" font-weight="800" fill="#fff">V</text>
      <text x="44" y="38" text-anchor="middle" font-family="Quicksand, sans-serif" font-size="15" font-weight="800" fill="#fff">S</text>`,
  },
  turkishComp: {
    ms: (p, u) => `${sh(u, 30, 52, 17, 3)}
      <rect x="15" y="9" width="4" height="42" rx="2" fill="url(#d${u})"/><circle cx="17" cy="9" r="2.6" fill="url(#a${u})"/>
      <path d="M19 11 H48 L41 19 L48 27 H19 Z" fill="url(#m${u})"/><path d="M19 11 H48 L41 19 L48 27 H19 Z" fill="url(#gl${u})"/>
      <path d="M31 14.5l1.3 3 3.2.3-2.4 2.1.8 3.1L31 20.9 28.1 23l.8-3.1-2.4-2.1 3.2-.3z" fill="#fff"/>`,
  },
  mathematics: {
    ms: (p, u) => `${sh(u, 32, 52, 20, 3)}
      <rect x="11" y="11" width="42" height="42" rx="9" fill="url(#m${u})"/><rect x="11" y="11" width="42" height="42" rx="9" fill="url(#gl${u})"/>
      <path d="M32 13v38M13 32h38" stroke="#fff" stroke-opacity="0.4" stroke-width="2"/>
      <g stroke="#fff" stroke-width="2.8" stroke-linecap="round">
        <path d="M21.5 21v10M16.5 26h10"/><path d="M37.5 26h10"/><path d="M18 38.5l7 7M25 38.5l-7 7"/><path d="M38 39.5h9M38 45h9"/>
      </g>`,
    hs: (p, u) => `${sh(u, 32, 50, 20, 3)}
      <path d="M9 35l6-3 8.5 21 11-35h20" fill="none" stroke="url(#m${u})" stroke-width="5.2" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M9 35l6-3 8.5 21 11-35h20" fill="none" stroke="#fff" stroke-opacity="0.25" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M39 29l10 12M49 29l-10 12" stroke="url(#d${u})" stroke-width="5.2" stroke-linecap="round"/>`,
  },
  science: {
    ms: (p, u) => `${sh(u, 32, 54, 17, 3)}
      <g transform="rotate(-45 32 30)"><rect x="29" y="42" width="6.5" height="17" rx="3.25" fill="url(#d${u})"/></g>
      <circle cx="30" cy="27" r="15.5" fill="url(#m${u})"/><circle cx="30" cy="27" r="10.5" fill="${p.cream}"/><circle cx="30" cy="27" r="10.5" fill="url(#gl${u})"/>
      <circle cx="30" cy="27" r="15.5" fill="none" stroke="url(#d${u})" stroke-width="3.4"/>
      <path d="M24 21a8 8 0 0 0-3 6" fill="none" stroke="#fff" stroke-opacity="0.85" stroke-width="2.6" stroke-linecap="round"/>`,
  },
  chemistry: {
    ms: (p, u) => `${sh(u, 32, 55, 22, 3)}
      <circle cx="30" cy="8" r="4" fill="url(#m${u})"/><circle cx="28.8" cy="6.8" r="1.3" fill="#fff" opacity="0.75"/>
      <circle cx="39" cy="12" r="3" fill="url(#m${u})"/><circle cx="38" cy="11" r="1" fill="#fff" opacity="0.75"/><circle cx="32" cy="16" r="2.3" fill="url(#m${u})"/>
      <path d="M26 22h12" stroke="${p.deep}" stroke-width="2.6" stroke-linecap="round"/>
      <path d="M27 23v8L12 50a3 3 0 0 0 2.7 4.6h34.6A3 3 0 0 0 52 50L37 31v-8z" fill="url(#c${u})"/><path d="M27 23v8L12 50a3 3 0 0 0 2.7 4.6h34.6A3 3 0 0 0 52 50L37 31v-8z" fill="url(#gl${u})"/>
      <path d="M18.5 40q6.5-3 13.5 0t13.5 0l6 10a3 3 0 0 1-2.7 4.6H14.7A3 3 0 0 1 12 50z" fill="url(#m${u})"/>
      <g fill="#fff" fill-opacity="0.6"><circle cx="24" cy="48" r="1.8"/><circle cx="33" cy="50" r="2.3"/><circle cx="39" cy="46.5" r="1.3"/><circle cx="29" cy="45" r="1"/></g>
      <path d="M24 27v13" stroke="#fff" stroke-opacity="0.5" stroke-width="2" stroke-linecap="round"/>`,
  },
  biology: {
    hs: (p, u) => `${sh(u, 32, 54, 15, 3)}
      <path d="M23 11c0 10 18 12 18 21s-18 11-18 21" fill="none" stroke="url(#m${u})" stroke-width="4" stroke-linecap="round"/>
      <path d="M41 11c0 10-18 12-18 21s18 11 18 21" fill="none" stroke="url(#d${u})" stroke-width="4" stroke-linecap="round"/>
      <path d="M26 18h12M22 27h20M22 37h20M26 46h12" stroke="${p.accent}" stroke-width="2.4" stroke-linecap="round"/>`,
  },
  physics: {
    hs: (p, u) => `${sh(u, 32, 54, 20, 3)}
      <g fill="none" stroke="url(#m${u})" stroke-width="3.2">
        <ellipse cx="32" cy="31" rx="22" ry="8.5"/><ellipse cx="32" cy="31" rx="22" ry="8.5" transform="rotate(60 32 31)"/><ellipse cx="32" cy="31" rx="22" ry="8.5" transform="rotate(120 32 31)"/>
      </g>
      <circle cx="51.5" cy="20.5" r="3.4" fill="#E8643B"/><circle cx="13" cy="40" r="3.4" fill="#F2C744"/><circle cx="50" cy="44.5" r="3.4" fill="#E8643B"/>
      <circle cx="32" cy="31" r="7" fill="#3FB4D8"/><circle cx="32" cy="31" r="7" fill="url(#gl${u})"/><circle cx="29.5" cy="28.5" r="2" fill="#fff" opacity="0.7"/>`,
  },
  socialStudies: {
    ms: (p, u) => `${sh(u, 32, 52, 20, 3)}
      <path d="M13 50V40a8 8 0 0 1 16 0v10z" fill="url(#a${u})"/><circle cx="21" cy="27" r="4.6" fill="url(#a${u})"/>
      <path d="M35 50V40a8 8 0 0 1 16 0v10z" fill="url(#m${u})"/><circle cx="43" cy="27" r="4.6" fill="url(#m${u})"/>
      <path d="M22 50V38a10 10 0 0 1 20 0v12z" fill="url(#d${u})"/><path d="M22 50V38a10 10 0 0 1 20 0v12z" fill="url(#gl${u})"/>
      <circle cx="32" cy="24" r="5.5" fill="url(#d${u})"/><circle cx="32" cy="24" r="5.5" fill="url(#gl${u})"/>`,
  },
  history: {
    ms: (p, u) => `${sh(u, 32, 53, 20, 3)}
      <path d="M10 23 L32 11 L54 23 Z" fill="url(#m${u})"/><path d="M10 23 L32 11 L54 23 Z" fill="url(#gl${u})"/>
      <rect x="12" y="23" width="40" height="4.5" rx="1.5" fill="url(#d${u})"/>
      <rect x="16" y="28" width="5" height="16" rx="1.5" fill="url(#m${u})"/><rect x="24.5" y="28" width="5" height="16" rx="1.5" fill="url(#m${u})"/><rect x="34.5" y="28" width="5" height="16" rx="1.5" fill="url(#m${u})"/><rect x="43" y="28" width="5" height="16" rx="1.5" fill="url(#m${u})"/>
      <rect x="16" y="28" width="32" height="16" rx="1.5" fill="url(#gl${u})"/><rect x="11" y="44" width="42" height="6" rx="2" fill="url(#d${u})"/>
      <path d="M9 50h46" stroke="${p.deep}" stroke-width="2.6" stroke-linecap="round"/>`,
  },
  geography: {
    ms: (p, u) => `${sh(u, 30, 58, 15, 3)}
      <path d="M30 44v9" stroke="url(#d${u})" stroke-width="3.4" stroke-linecap="round"/>
      <path d="M19.5 56q10.5-7 21 0a2 2 0 0 1-2 2.4H21.5a2 2 0 0 1-2-2.4z" fill="url(#d${u})"/>
      <circle cx="30" cy="27" r="17" fill="url(#m${u})"/><circle cx="30" cy="27" r="17" fill="url(#gl${u})"/>
      <path d="M22 19c3-1 6 1 5 4s-4 3-3 6-2 5-4 4-3-5-2-9 2-4 4-5z" fill="${p.deep}" opacity="0.5"/>
      <path d="M37 21c2 1 2 3 1 5s2 3 0 5-1 5-3 4-2-4-1-6-2-3 0-5 1-3 3-3z" fill="${p.deep}" opacity="0.5"/>
      <path d="M13 27h34M30 10v34" stroke="#fff" stroke-opacity="0.45" stroke-width="1.2"/>
      <path d="M45 13a19 19 0 0 1 0 28" fill="none" stroke="url(#d${u})" stroke-width="3" stroke-linecap="round"/>`,
  },
  turkish: {
    ms: (p, u) => `${sh(u, 32, 52, 16, 3)}
      <rect x="18" y="11" width="28" height="40" rx="7" fill="url(#m${u})"/><rect x="18" y="11" width="28" height="40" rx="7" fill="url(#gl${u})"/>
      <text x="32" y="40" text-anchor="middle" font-family="Quicksand, sans-serif" font-size="26" font-weight="800" fill="#fff">A</text>`,
  },
  literature: {
    hs: (p, u) => `${sh(u, 30, 55, 18, 3)}
      <path d="M52 12c-18-2-33 11-37 27l5 5C36 33 49 28 52 12z" fill="url(#m${u})"/><path d="M52 12c-18-2-33 11-37 27l5 5C36 33 49 28 52 12z" fill="url(#gl${u})"/>
      <path d="M52 12C40 22 30 32 20 44" stroke="${p.deep}" stroke-width="1.8" stroke-linecap="round" fill="none" opacity="0.55"/>
      <path d="M34 17v7M44 26l-9 1M30 38l-8 1" stroke="${p.deep}" stroke-width="1.6" stroke-linecap="round" opacity="0.5"/>
      <path d="M20 44 9 55" stroke="url(#d${u})" stroke-width="4.5" stroke-linecap="round"/><path d="M11.5 52.5l3 3-5 1.5z" fill="${p.deep}"/>`,
  },
  english: {
    ms: (p, u) => `${sh(u, 32, 53, 18, 3)}
      <circle cx="32" cy="31" r="20" fill="url(#m${u})"/><circle cx="32" cy="31" r="20" fill="url(#gl${u})"/>
      <path d="M12 31h40M32 11v40" stroke="#fff" stroke-opacity="0.6" stroke-width="1.5"/>
      <path d="M32 11c-10 6-10 34 0 40M32 11c10 6 10 34 0 40" fill="none" stroke="#fff" stroke-opacity="0.6" stroke-width="1.4"/>
      <path d="M14 24h36M14 38h36" fill="none" stroke="#fff" stroke-opacity="0.45" stroke-width="1.2"/>`,
    hs: (p, u) => `${sh(u, 32, 50, 18, 3)}
      <path d="M11 12h42a5 5 0 0 1 5 5v18a5 5 0 0 1-5 5H29l-11 9v-9h-7a5 5 0 0 1-5-5V17a5 5 0 0 1 5-5z" fill="url(#m${u})"/>
      <path d="M11 12h42a5 5 0 0 1 5 5v18a5 5 0 0 1-5 5H29l-11 9v-9h-7a5 5 0 0 1-5-5V17a5 5 0 0 1 5-5z" fill="url(#gl${u})" opacity="0.5"/>
      <circle cx="23" cy="26" r="2.8" fill="#fff"/><circle cx="32" cy="26" r="2.8" fill="#fff"/><circle cx="41" cy="26" r="2.8" fill="#fff"/>`,
  },
  religiousCulture: {
    ms: (p, u) => `${sh(u, 32, 53, 21, 3)}
      <rect x="46" y="16" width="6" height="34" rx="2" fill="url(#d${u})"/><rect x="46" y="16" width="6" height="34" rx="2" fill="url(#gl${u})" opacity="0.5"/>
      <path d="M46 16a3 3 0 0 1 6 0z" fill="url(#a${u})"/><circle cx="49" cy="11.5" r="1.7" fill="url(#a${u})"/>
      <rect x="13" y="30" width="32" height="20" rx="2" fill="url(#m${u})"/><rect x="13" y="30" width="32" height="20" rx="2" fill="url(#gl${u})" opacity="0.4"/>
      <path d="M18 30a11 11 0 0 1 22 0z" fill="url(#d${u})"/><path d="M18 30a11 11 0 0 1 22 0z" fill="url(#gl${u})" opacity="0.5"/>
      <path d="M29 12.5v6" stroke="${p.deep}" stroke-width="2" stroke-linecap="round"/><circle cx="29" cy="11" r="2.1" fill="url(#a${u})"/>
      <path d="M25 50V42a4 4 0 0 1 8 0v8z" fill="url(#d${u})"/><path d="M7 50h50" stroke="${p.deep}" stroke-width="2.6" stroke-linecap="round"/>`,
    hs: (p, u) => `${sh(u, 32, 52, 17, 3)}
      <ellipse cx="32" cy="28" rx="15" ry="13" fill="none" stroke="${p.deep}" stroke-opacity="0.3" stroke-width="1.2"/>
      <g><circle cx="32" cy="15" r="2.9" fill="url(#m${u})"/><circle cx="39.5" cy="16.7" r="2.9" fill="url(#d${u})"/><circle cx="45" cy="21.5" r="2.9" fill="url(#m${u})"/><circle cx="47" cy="28" r="2.9" fill="url(#d${u})"/><circle cx="45" cy="34.5" r="2.9" fill="url(#m${u})"/><circle cx="39.5" cy="39.3" r="2.9" fill="url(#d${u})"/><circle cx="24.5" cy="39.3" r="2.9" fill="url(#d${u})"/><circle cx="19" cy="34.5" r="2.9" fill="url(#m${u})"/><circle cx="17" cy="28" r="2.9" fill="url(#d${u})"/><circle cx="19" cy="21.5" r="2.9" fill="url(#m${u})"/><circle cx="24.5" cy="16.7" r="2.9" fill="url(#d${u})"/></g>
      <circle cx="32" cy="44" r="4" fill="url(#a${u})"/><path d="M32 48v6M29.5 48l-1 5.5M34.5 48l1 5.5" stroke="${p.deep}" stroke-width="1.7" stroke-linecap="round"/>`,
  },
  philosophy: {
    ms: (p, u) => `${sh(u, 32, 57, 16, 3)}
      <ellipse cx="32" cy="32" rx="15.5" ry="19" fill="none" stroke="url(#m${u})" stroke-width="6.5"/><ellipse cx="32" cy="32" rx="15.5" ry="19" fill="none" stroke="#fff" stroke-opacity="0.25" stroke-width="2"/>
      <path d="M32 7V57" stroke="url(#m${u})" stroke-width="6.5" stroke-linecap="round"/>
      <path d="M24 8q8 4 16 0M24 56q8-4 16 0" fill="none" stroke="url(#d${u})" stroke-width="5" stroke-linecap="round"/>
      <path d="M27 14c-6 2.5-10 9-10 18" fill="none" stroke="#fff" stroke-opacity="0.4" stroke-width="2" stroke-linecap="round"/>`,
  },
};

const achievementSurface: React.CSSProperties = {
  borderRadius: "50%",
  position: "relative",
  background: "radial-gradient(120% 120% at 32% 16%, #ffffff 0%, #fbfdfe 70%, #f3f8fa 100%)",
  border: "1px solid rgba(255,255,255,0.95)",
  boxShadow: [
    "inset 0 2px 3px rgba(255,255,255,0.95)",
    "inset 0 -11px 20px -13px rgba(26,58,69,0.10)",
    "0 2px 4px rgba(26,58,69,0.10)",
    "0 8px 16px -6px rgba(26,58,69,0.26)",
    "0 20px 34px -16px rgba(26,58,69,0.28)",
  ].join(", "),
};

function SubjectAchievementBase({
  subject = "mathematics",
  level = "ms",
  size = 96,
  title,
  ...rest
}: {
  subject?: string;
  level?: string;
  size?: number;
  title?: string;
  [key: string]: any;
}) {
  const rawId = (React as any).useId ? (React as any).useId() : "b";
  const u = String(rawId).replace(/[^a-zA-Z0-9]/g, "");
  // The SVG markup is a pure function of (subject, level, u) — u is stable per instance — so build it
  // (defs/illo + the two regex passes + fragment concatenation) only when those change, not on every
  // parent re-render. These icons render per-row in homework/dashboard lists, so an un-memoized rebuild
  // re-runs the full string build + regex for every visible icon on any parent re-render.
  const { inner, label } = React.useMemo(() => {
    const meta = SUBJECTS[subject] || SUBJECTS.mathematics;
    const subjKey = SUBJECTS[subject] ? subject : "mathematics";
    const lvl = TWO_LEVEL[subjKey]
      ? LEVEL_ALIAS[String(level).toLowerCase()] || "ms"
      : UNIFIED[subjKey] || "ms";
    const illoForSubject = ILLO[subjKey] || ILLO.mathematics;
    const illoFn = illoForSubject[lvl] || illoForSubject.ms || Object.values(illoForSubject)[0];
    const raw = illoFn(meta, u) || "";
    const shadows = (raw.match(/<ellipse[^>]*opacity="0\.1[0-9]"[^>]*\/>/g) || []).join("");
    const scene = raw.replace(/<ellipse[^>]*opacity="0\.1[0-9]"[^>]*\/>/g, "");
    const built =
      defs(meta, u) +
      shadows +
      `<circle cx="32" cy="32" r="23" fill="url(#glow${u})" filter="url(#bl${u})"/>` +
      `<circle cx="32" cy="32" r="20.5" fill="#ffffff" opacity="0.72" filter="url(#bl${u})"/>` +
      `<g filter="url(#dp${u})"><g filter="url(#vol${u})">` + scene + `</g></g>` +
      `<ellipse cx="25" cy="18" rx="12" ry="6.5" fill="#ffffff" opacity="0.22" filter="url(#bl${u})" transform="rotate(-18 25 18)"/>`;
    return { inner: built, label: meta.label };
  }, [subject, level, u]);
  const art = Math.round(size * 0.82);

  return (
    <span
      {...rest}
      title={title || label}
      role="img"
      aria-label={title || label + " illustration"}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        flexShrink: 0,
        ...achievementSurface,
        ...(rest.style || {}),
      }}
    >
      <svg
        width={art}
        height={art}
        viewBox="0 0 64 64"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          display: "block",
          overflow: "visible",
          filter: "saturate(1.04) brightness(0.99) drop-shadow(0 5px 6px rgba(26,58,69,0.32))",
          position: "relative",
          zIndex: 1,
        }}
        dangerouslySetInnerHTML={{ __html: inner }}
      />
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          pointerEvents: "none",
          zIndex: 2,
          background: "linear-gradient(150deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 38%)",
        }}
      />
    </span>
  );
}

// dev coterieType → design SubjectAchievement subject key (null = no illustration → use fallback).
const COTERIE_TO_SUBJECT: Record<string, string> = {
  MATHEMATICS: "mathematics",
  GEOMETRY: "mathematics",
  SCIENCE: "science",
  PHYSICS: "physics",
  CHEMISTRY: "chemistry",
  BIOLOGY: "biology",
  TURKISH: "turkish",
  LITERATURE: "literature",
  ENGLISH: "english",
  HISTORY: "history",
  GEOGRAPHY: "geography",
  SOCIAL_STUDIES: "socialStudies",
  PHILOSOPHY: "philosophy",
  GENERAL_CULTURE: "generalKnowledge",
  RELIGION_CULTURE_AND_ETHICS: "religiousCulture",
  LIFE_STUDIES: "socialStudies",
};

export const subjectKeyForCoterie = (coterieType?: string): string | null =>
  (coterieType && COTERIE_TO_SUBJECT[coterieType]) || null;

// Memoized so a parent re-render with the same props skips the component entirely (the SVG build is
// already useMemo'd, but React.memo avoids even re-running the body for stable per-row icons).
export const SubjectAchievement = React.memo(SubjectAchievementBase);

export default SubjectAchievement;
